import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, Minus } from 'lucide-react';
import { setPendingPlan } from '../../lib/pendingPlan';
import { useAuth } from '../../context/AuthContext';
import { TIER_PRICING, annualSavingPercent, monthlyPrice } from '../../config/tiers';
import type { BillingPeriod, PaidTier } from '../../config/tiers';

/**
 * Landing pricing: three plan cards, then a feature comparison table.
 *
 * The cards carry only name, price, subtitle and button. Feature lists on the
 * cards would duplicate the table below and then drift from it, and with three
 * tiers they grow long enough to push the buttons out of alignment.
 *
 * ── The table at 400px ───────────────────────────────────────────────────────
 *
 * Horizontal scroll is the usual answer for a wide table and the wrong one here:
 * comparing means seeing the tier columns side by side, and scrolling hides the
 * very thing being compared. So the layout is a CSS grid whose tier columns
 * narrow rather than overflow, the tier header is sticky, and each section
 * collapses so a long table stays navigable on a phone.
 *
 * ONE grid template drives the sticky header and every row, so a tick can never
 * drift out of alignment with the column it belongs to.
 */

// Feature column, then one narrow column per tier. The tier columns shrink on a
// phone; the feature column takes what is left and wraps.
const GRID_SM = 'minmax(0,1fr) repeat(3, 46px)';
const GRID_MD = 'minmax(0,1fr) repeat(3, 116px)';

const CREAM = '#f4f1ea';
const GOLD = '#d4b483';
const MUTED = 'rgba(244,241,234,0.6)';
const FAINT = 'rgba(244,241,234,0.4)';
const HAIRLINE = '0.5px solid rgba(255,255,255,0.1)';

interface Row {
  label: string;
  free: boolean;
  plus: boolean;
  pro: boolean;
}

interface Section {
  title: string;
  rows: Row[];
}

type Flags = Pick<Row, 'free' | 'plus' | 'pro'>;
const ALL: Flags = { free: true, plus: true, pro: true };
const PAID: Flags = { free: false, plus: true, pro: true };
const PRO_ONLY: Flags = { free: false, plus: false, pro: true };

const SECTIONS: Section[] = [
  {
    title: 'Tracking',
    rows: [
      { label: 'Cryptocurrencies, plus popular shares, ETFs and metals', ...ALL },
      { label: 'Portfolio dashboard and charts', ...ALL },
      { label: 'Unlimited transactions', ...ALL },
      { label: 'Asset and wallet allocation', ...ALL },
      { label: 'Cash-out goal tracking', ...ALL },
    ],
  },
  {
    title: 'Planning',
    rows: [
      { label: 'Split buy and split sell planners', ...PAID },
      { label: 'Compare up to 5 assets side by side', ...PAID },
      { label: 'Multi-wallet breakdown per asset', ...PAID },
    ],
  },
  {
    title: 'Australian tax',
    rows: [
      { label: 'See the CGT on every sale you have made', ...PRO_ONLY },
      { label: 'Work out the tax before you sell', ...PRO_ONLY },
      { label: 'CGT reports for your accountant', ...PRO_ONLY },
    ],
  },
];

const TIER_COLUMNS: { key: keyof Flags; label: string }[] = [
  { key: 'free', label: 'Free' },
  { key: 'plus', label: 'Plus' },
  { key: 'pro', label: 'Pro' },
];

/** Whole dollars stay whole: "A$8", not "A$8.00". */
function aud(amount: number): string {
  return `A$${amount % 1 === 0 ? amount : amount.toFixed(2)}`;
}

/**
 * One tick or dash. The glyph carries no meaning to a screen reader, so each cell
 * also states plainly whether that tier includes the feature.
 */
function Cell({ on, tier, feature }: { on: boolean; tier: string; feature: string }) {
  return (
    <div className="flex items-center justify-center py-3">
      <span className="sr-only">{`${feature}: ${on ? 'included' : 'not included'} on ${tier}`}</span>
      {on ? (
        <Check className="w-4 h-4" style={{ color: GOLD }} strokeWidth={2.5} aria-hidden="true" />
      ) : (
        <Minus className="w-4 h-4" style={{ color: 'rgba(244,241,234,0.22)' }} aria-hidden="true" />
      )}
    </div>
  );
}

interface CardProps {
  name: string;
  subtitle: string;
  price: string;
  priceSuffix: string;
  /** Second line under the price, e.g. "A$96 billed annually". */
  note?: string;
  cta: string;
  to: string;
  onClick?: () => void;
  featured?: boolean;
}

function PlanCard({ name, subtitle, price, priceSuffix, note, cta, to, onClick, featured }: CardProps) {
  return (
    <div
      className="rounded-xl flex flex-col"
      style={{
        background: featured
          ? 'linear-gradient(180deg, rgba(212,180,131,0.12), rgba(255,255,255,0.05))'
          : 'rgba(255,255,255,0.06)',
        border: featured ? '1px solid rgba(212,180,131,0.4)' : '1px solid rgba(255,255,255,0.14)',
        padding: 28,
      }}
    >
      {/* Badge sits INLINE with the name rather than above it: on its own line it
          would push the Pro card's price and button down out of line with the
          other two, and the three buttons are meant to sit on one row. */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[18px] font-semibold" style={{ color: CREAM }}>
          {name}
        </h3>
        {featured && (
          <span
            className="text-[10px] font-semibold uppercase px-2 py-1 rounded-full whitespace-nowrap"
            style={{ backgroundColor: 'rgba(212,180,131,0.16)', color: GOLD, letterSpacing: '0.08em' }}
          >
            Recommended
          </span>
        )}
      </div>
      <p className="mt-1 text-[13px]" style={{ color: 'rgba(244,241,234,0.55)' }}>
        {subtitle}
      </p>

      <div className="mt-5 flex items-baseline gap-1.5">
        <span
          className="font-semibold tabular-nums"
          style={{ color: CREAM, fontSize: 36, letterSpacing: '-0.02em' }}
        >
          {price}
        </span>
        <span className="text-[13px]" style={{ color: 'rgba(244,241,234,0.5)' }}>
          {priceSuffix}
        </span>
      </div>

      {/* Reserved whether or not there is a note, so the three buttons stay on one
          line as the billing toggle changes. */}
      <div
        className="mt-1 text-[12px]"
        style={{ color: 'rgba(244,241,234,0.5)', minHeight: 18 }}
        aria-hidden={note ? undefined : true}
      >
        {note ?? ' '}
      </div>

      <Link
        to={to}
        onClick={onClick}
        className="mt-5 block text-center text-[13px] font-medium px-4 py-2.5 rounded-lg transition-transform hover:scale-[1.01]"
        style={
          featured
            ? { backgroundColor: CREAM, color: '#0c0e12' }
            : {
                backgroundColor: 'rgba(255,255,255,0.06)',
                color: CREAM,
                border: '0.5px solid rgba(255,255,255,0.08)',
              }
        }
      >
        {cta}
      </Link>
    </div>
  );
}

export function PricingSection() {
  const [billing, setBilling] = useState<BillingPeriod>('annual');
  const isAnnual = billing === 'annual';

  // Every section starts expanded: the table is the part that does the
  // convincing, so nothing is hidden behind a tap by default. Collapsing exists
  // to shorten a long page on a phone once a section has been read.
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const toggle = (title: string) => setClosed((c) => ({ ...c, [title]: !c[title] }));

  // New-customer CTAs: logged-out visitors go to the SIGNUP form, logged-in ones
  // straight to the app (AuthPage/RequireAuth never render auth for them).
  const { session } = useAuth();
  const authTarget = session ? '/app' : '/login?mode=signup';

  const paidCard = (tier: PaidTier, subtitle: string, featured: boolean) => (
    <PlanCard
      name={tier === 'plus' ? 'Plus' : 'Pro'}
      subtitle={subtitle}
      price={aud(monthlyPrice(tier, billing))}
      priceSuffix="/month"
      note={isAnnual ? `A$${TIER_PRICING[tier].annual} billed annually` : undefined}
      cta={tier === 'plus' ? 'Get Plus' : 'Get Pro'}
      to={authTarget}
      onClick={() => setPendingPlan({ tier, period: billing })}
      featured={featured}
    />
  );

  return (
    <section
      id="pricing"
      className="px-6 py-24"
      style={{ borderTop: '0.5px solid rgba(255,255,255,0.06)' }}
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[11px] font-semibold tracking-[0.18em] mb-4" style={{ color: GOLD }}>
            PRICING
          </div>
          <h2
            className="font-semibold"
            style={{
              color: CREAM,
              fontSize: 'clamp(26px, 4vw, 36px)',
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
            }}
          >
            {/* Each sentence is its own inline-block so a wrap falls BETWEEN them.
                Left as plain text it breaks mid-sentence at both phone and desktop
                widths ("Tax tools" stranded on the first line). */}
            <span className="inline-block">Built for investors.</span>{' '}
            <span className="inline-block">Tax tools built for Australia.</span>
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed" style={{ color: MUTED }}>
            Track crypto, ASX shares, ETFs and metals in one place. The tax tools follow
            Australian CGT rules and are for Australian tax residents.
          </p>
        </div>

        {/* Monthly / Annual billing toggle */}
        <div className="mt-10 flex justify-center">
          <div
            className="inline-flex items-center gap-1 rounded-full p-1"
            style={{
              backgroundColor: 'rgba(255,255,255,0.06)',
              border: '0.5px solid rgba(255,255,255,0.12)',
            }}
          >
            {(['monthly', 'annual'] as const).map((b) => {
              const active = billing === b;
              return (
                <button
                  key={b}
                  type="button"
                  onClick={() => setBilling(b)}
                  className="flex items-center gap-1.5 text-[13px] font-medium px-4 py-1.5 rounded-full transition-colors"
                  style={active ? { backgroundColor: CREAM, color: '#0c0e12' } : { color: MUTED }}
                >
                  {b === 'monthly' ? 'Monthly' : 'Annual'}
                  {b === 'annual' && (
                    <span
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: GOLD, color: '#0c0e12' }}
                    >
                      Save {annualSavingPercent('pro')}%
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Three plan cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <PlanCard
            name="Free"
            subtitle="Track your portfolio"
            price="A$0"
            priceSuffix="/forever"
            cta="Get started"
            to={authTarget}
          />
          {paidCard('plus', 'Plan your entries and exits', false)}
          {paidCard('pro', 'Everything in Plus, plus Australian tax', true)}
        </div>

        {/* ── Comparison table ───────────────────────────────────────────────── */}
        <div className="mt-16">
          {/* Tier header, sticky so the columns stay identified while scrolling a
              long table. That is what lets each section drop its own headings on
              a phone instead of repeating Free/Plus/Pro three times. */}
          <div
            className="grid sticky top-0 z-10 md:[grid-template-columns:var(--cols-md)]"
            style={{
              gridTemplateColumns: GRID_SM,
              borderBottom: HAIRLINE,
              backgroundColor: '#0c0e12',
              ['--cols-md' as string]: GRID_MD,
            }}
          >
            <div className="py-3 text-[11px] font-semibold tracking-[0.14em]" style={{ color: FAINT }}>
              COMPARE
            </div>
            {TIER_COLUMNS.map((t) => (
              <div
                key={t.key}
                className="py-3 text-center text-[12px] font-semibold"
                style={{ color: CREAM }}
              >
                {t.label}
              </div>
            ))}
          </div>

          {SECTIONS.map((section) => {
            const isClosed = !!closed[section.title];
            return (
              <div key={section.title} style={{ borderBottom: HAIRLINE }}>
                {/* Collapsible on a phone. On md+ the content is forced visible and
                    the chevron hidden, so this reads as a heading rather than a
                    control that appears to do nothing. */}
                <button
                  type="button"
                  onClick={() => toggle(section.title)}
                  aria-expanded={!isClosed}
                  className="w-full flex items-center justify-between gap-3 py-3.5 text-left md:cursor-default"
                >
                  <span className="text-[13px] font-semibold" style={{ color: GOLD }}>
                    {section.title}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 md:hidden transition-transform ${isClosed ? '' : 'rotate-180'}`}
                    style={{ color: FAINT }}
                    aria-hidden="true"
                  />
                </button>

                <div className={isClosed ? 'hidden md:block' : 'block'}>
                  {section.rows.map((row) => (
                    <div
                      key={row.label}
                      className="grid items-center md:[grid-template-columns:var(--cols-md)]"
                      style={{
                        gridTemplateColumns: GRID_SM,
                        borderTop: '0.5px solid rgba(255,255,255,0.05)',
                        ['--cols-md' as string]: GRID_MD,
                      }}
                    >
                      <div className="py-3 pr-3 text-[13px] leading-snug" style={{ color: MUTED }}>
                        {row.label}
                      </div>
                      {TIER_COLUMNS.map((t) => (
                        <Cell key={t.key} on={row[t.key]} tier={t.label} feature={row.label} />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <p className="mt-5 text-[12px] leading-relaxed" style={{ color: FAINT }}>
            The tax tools follow Australian CGT rules and are intended for Australian tax
            residents.
          </p>
          {/* Overseas visitors can set any display currency inside the app, so the
              AUD charge would otherwise be a surprise at the Stripe checkout. Stated
              here rather than near the prices: a payment term, not a selling point. */}
          <p className="mt-2 text-[12px] leading-relaxed" style={{ color: FAINT }}>
            Prices are in Australian dollars and billing is always in AUD, whichever display
            currency you choose in the app.
          </p>
        </div>
      </div>
    </section>
  );
}
